import { CONFIG_OPTION_TOAST_ERROR, CONFIG_OPTION_TOAST_NORMAL } from "common/toast";
import Hashtag from "components/Common/Hashtag";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import React, { useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Col, Container, Input, Label, Row, Spinner } from 'reactstrap';
import { postImageLibrary, postUploadFileMulti } from "store/thunks";
import * as Yup from "yup";

export interface Props {
    triggerRefresh?: () => void;
}

registerLocale("en", en);
registerLocale("ko", ko);

const FILE_SIZE = 11 * 1024 * 1024; // 11MB
const SUPPORTED_FORMATS = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];

const LibraryForm = ({
    triggerRefresh,
}: Props) => {
    const { t } = useTranslation();

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const handleSubmit = async (values: any) => {
        try {
            setIsLoading((_prev) => true);
            let res_files: any = [];
            if (values?.files?.length) {
                const res: any = await postUploadFileMulti({ files: values?.files, }); // action: 10
                res_files = (res?.data || [])?.map((item: any) => (
                    {
                        storage_path: item?.filepath,
                        file_name: item?.filename
                    }
                ));
            };
            const data = {
                files: res_files,
                tags: (values?.tags || [])?.map((url: any) => url?.text),
            };
            const response: any = await postImageLibrary(data);
            if (response?.code === 200) {
                setIsLoading((_prev) => false);
                toast(
                    `${t("The process has been completed.")}`,
                    CONFIG_OPTION_TOAST_NORMAL
                );
                triggerRefresh && triggerRefresh();
            } else {
                setIsLoading((_prev) => false);
                toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
            }
        } catch (error: any) {
            setIsLoading((_prev) => false);
            toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
            return error;
        }
    };

    const validationSchema = Yup.object({
        files: Yup.array()
            .of(
                Yup.mixed()
                    .test(
                        'fileSize',
                        t('File too large. Max size is 10MB.'),
                        (value: any) => !value || (value?.size <= FILE_SIZE)
                    )
                    .test(
                        'fileFormat',
                        t('Unsupported Format. Please upload an Image file.'),
                        (value: any) => !value || SUPPORTED_FORMATS.includes(value?.type)
                    )
            ).min(1, t('Please select at least 1 image')),
        tags: Yup.array().of(
            Yup.object().shape({
                id: Yup.string(),
                text: Yup.string(),
            })
        ).min(1, t('Please enter at least 1 tag')),
    });

    const formik = useFormik({
        initialValues: {
            files: [],
            tags: [],
        },
        validationSchema,
        onSubmit: handleSubmit,
    });

    const handleChangeFile = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            const fileArray = Array.from(files);
            formik.setFieldValue("files", [...formik.values.files, ...fileArray]);
        } else {
            formik.setFieldValue("files", []);
        }
    };

    const handleDeleteFile = (index: number) => {
        if (isLoading) {
            return;
        }
        const fileArray = formik.values.files?.filter((f, i) => i !== index);
        formik.setFieldValue("files", fileArray);
    }

    return (
        <React.Fragment>
            <div>
                <Container>
                    <Row>
                        <form onSubmit={formik.handleSubmit}>
                            <div style={{ margin: '0 auto' }}>
                                <Row>
                                    <Col sm={12} md={12} xl={12}>
                                        <Label htmlFor="formSizeDefault" className="form-label">{t('Image')} <span className="text-danger"> *</span></Label>
                                        <div className="d-flex gap-3 flex-wrap">
                                            {
                                                (formik.values.files || [])?.map((file: any, index: number) => (
                                                    <div className="position-relative" style={{ width: '200px', height: '200px', backgroundColor: '#eaeaea', overflow: 'hidden', borderRadius: 'var(--vz-border-radius)' }}>
                                                        <img
                                                            src={URL.createObjectURL(file)}
                                                            alt={`preview-${index}`}
                                                            style={{ width: "100%", height: "100%", objectFit: 'cover' }}
                                                        />
                                                        <button type="button" className="position-absolute btn btn-sm btn-soft-danger edit-item-btn"
                                                            style={{
                                                                top: '10px',
                                                                right: '10px',
                                                                width: '30px',
                                                                height: '30px',
                                                                borderRadius: '15px',
                                                            }}
                                                            onClick={() => handleDeleteFile(index)}
                                                        ><i className="ri-delete-bin-3-fill align-bottom"></i></button>
                                                    </div>
                                                ))
                                            }
                                            <div className="custom-file-image-button">
                                                <Input
                                                    className="form-control"
                                                    accept="image/jpeg, image/png, image/gif, image/webp, image/svg+xml"
                                                    label="Upload Your Image"
                                                    data-title="Upload Your Image"
                                                    id="formSizeDefault"
                                                    type="file"
                                                    multiple
                                                    disabled={isLoading}
                                                    onChange={handleChangeFile}
                                                />
                                                {<div className="text-upload">  <i className="ri-upload-line me-2" /> {formik.values.files?.length ? t('Add Image') : t('No file chosen')}</div>}
                                            </div>
                                        </div>
                                        {formik.touched.files && formik.errors.files ? (
                                            <div className="text-danger mt-2">{formik.errors.files}</div>
                                        ) : null}
                                    </Col>
                                    <Col sm={12} md={12} xl={12} className='mt-3 '>
                                        <Label htmlFor="tags" className="form-label">{t('Tags')} <span className="text-danger"> *</span></Label>
                                        <Hashtag
                                            name="tags"
                                            placeholder={`${t("Tags")}...`}
                                            suggestions={[]}
                                            initialValue={formik?.values?.tags || []}
                                            onChangeTag={(event: any) => {
                                                formik.setFieldValue("tags", event);
                                            }}
                                            idSelected="hashtags-CampaignFormMulti"
                                            maxHashtags={2000}
                                            maxHeight="150px"
                                        />
                                        {formik.touched.tags && formik.errors.tags ? (
                                            <div className="text-danger mt-2">{formik.errors.tags}</div>
                                        ) : null}
                                    </Col>
                                    <Col sm={12} md={12} xl={12} className='mt-4 pt-1 text-end'>
                                        <button
                                            style={{ width: '160px' }}
                                            type="submit"
                                            disabled={isLoading}
                                            className="btn btn-success fs-14 rounded-pill">
                                            {isLoading ? <Spinner size="sm me-2" ></Spinner> : <></>}
                                            {t('Button Upload Image')}
                                        </button>
                                    </Col>
                                </Row>
                            </div>
                        </form>
                    </Row >
                </Container >
            </div>
            <ToastContainer closeButton={false} newestOnTop={false} pauseOnFocusLoss autoClose={2000} limit={2} />
        </React.Fragment >
    );
};

export default LibraryForm;
